import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Send, CheckCircle, AlertCircle, Phone, Mail, MapPin } from 'lucide-react';
import SectionWrapper from '../../components/marketing/SectionWrapper';
import api from '../../services/api';

export default function ContactPage() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    name: '',
    organization: '',
    phone: '',
    email: '',
    role: '',
    message: '',
  });
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('sending');
    try {
      await api.post('/contact', form);
      setStatus('success');
      setForm({ name: '', organization: '', phone: '', email: '', role: '', message: '' });
    } catch {
      setStatus('error');
    }
  };

  const roleOptions = [
    { value: 'parent', label: t('contact.roles.parent') },
    { value: 'kindergarten', label: t('contact.roles.kindergarten') },
    { value: 'network', label: t('contact.roles.network') },
    { value: 'municipality', label: t('contact.roles.municipality') },
    { value: 'other', label: t('contact.roles.other') },
  ];

  const inputClass = 'w-full px-3.5 py-2.5 rounded-lg border border-gray-200 text-sm focus:border-cyan-500 focus:ring-2 focus:ring-cyan-100 outline-none transition-all bg-white';

  return (
    <>
      <section className="pt-24 pb-14 bg-gradient-to-br from-cyan-50/60 via-white to-teal-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-3 tracking-tight">{t('contact.title')}</h1>
            <p className="text-base md:text-lg text-gray-500 max-w-xl mx-auto">{t('contact.subtitle')}</p>
          </motion.div>
        </div>
      </section>

      <SectionWrapper background="white">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
          <div className="lg:col-span-2">
            <motion.form
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="bg-white rounded-xl shadow-sm ring-1 ring-gray-100 p-5 md:p-7"
            >
              {status === 'success' ? (
                <div className="text-center py-10">
                  <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                  <h3 className="text-lg font-bold text-gray-900 mb-1">{t('contact.success')}</h3>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.name')} *</label>
                      <input type="text" required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.organization')}</label>
                      <input type="text" value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })} className={inputClass} />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.phone')} *</label>
                      <input type="tel" required dir="ltr" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className={inputClass} />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.email')} *</label>
                      <input type="email" required dir="ltr" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className={inputClass} />
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.role')}</label>
                    <select value={form.role} onChange={e => setForm({ ...form, role: e.target.value })} className={`${inputClass} bg-white`}>
                      <option value="">--</option>
                      {roleOptions.map(o => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="mb-5">
                    <label className="block text-xs font-medium text-gray-600 mb-1">{t('contact.message')} *</label>
                    <textarea required rows={4} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} className={`${inputClass} resize-none`} />
                  </div>

                  {status === 'error' && (
                    <div className="flex items-center gap-2 text-red-600 text-xs mb-3">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {t('contact.error')}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={status === 'sending'}
                    className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-cyan-600 text-white font-semibold rounded-xl text-sm hover:bg-cyan-700 transition-all shadow-md shadow-cyan-600/20 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {status === 'sending' ? t('contact.sending') : (<><Send className="w-4 h-4" />{t('contact.send')}</>)}
                  </button>
                </>
              )}
            </motion.form>
          </div>

          <div>
            <motion.div initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }} className="space-y-4">
              {[
                { icon: Phone, label: t('footer.phone'), value: '050-5774499', dir: 'ltr' as const },
                { icon: Mail, label: t('footer.email'), value: 'info@kipikid.com' },
                { icon: MapPin, label: t('footer.address'), value: 'Israel' },
              ].map((item, i) => (
                <div key={i} className="bg-gray-50/80 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-cyan-50 flex items-center justify-center">
                      <item.icon className="w-4 h-4 text-cyan-600" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-400">{item.label}</p>
                      <p className="text-sm font-semibold text-gray-900" dir={item.dir}>{item.value}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div className="bg-gray-100 rounded-xl h-40 flex items-center justify-center text-gray-300">
                <MapPin className="w-8 h-8" />
              </div>
            </motion.div>
          </div>
        </div>
      </SectionWrapper>
    </>
  );
}
